import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rolesApi } from '../../../api/admin/roles';
import { permissionCatalogueApi } from '../../../api/admin/permission-catalogue';
import { ApiError } from '../../../api/client';
import { IconActionButton } from '../../../components/IconActionButton';
import { X } from 'lucide-react';
import { SectionHeader } from '../../../components/SectionHeader';
import { PageHeader } from '../../../components/PageHeader';
import { DataTable } from '../../../components/DataTable';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';
import { PermissionScope } from '../../../types/admin';
import { genericDocumentApi } from '../../../api/generic-documents';
import { CommentsThread } from '../../../components/CommentsThread';
import { DocumentsSection } from '../../../components/DocumentsSection';
import { AuditHistoryTab } from '../../../components/AuditHistoryTab';

const scopes: PermissionScope[] = ['own', 'project', 'department', 'location', 'country', 'all'];

const formSchema = z.object({
  permissionKey: z.string().min(1, 'Choose a permission to grant.'),
  scope: z.enum(['own', 'project', 'department', 'location', 'country', 'all']),
});
type FormValues = z.infer<typeof formSchema>;

export function RolePermissionsPage() {
  const { roleId } = useParams<{ roleId: string }>();
  const id = Number(roleId);
  const canManage = usePermission('admin.permissions.manage');
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const permissionsQuery = useQuery({ queryKey: ['role-permissions', id], queryFn: () => rolesApi.permissions(id) });
  const catalogueQuery = useQuery({ queryKey: ['permission-catalogue'], queryFn: () => permissionCatalogueApi.list() });
  const docsQuery = useQuery({ queryKey: ['role-documents', id], queryFn: () => genericDocumentApi.listFor('role', id) });

  /**
   * The real fix this page needed: the Permission Catalogue (180
   * entries with friendly labels/descriptions) has existed on the
   * backend since it was built, but this page never consumed it - an
   * administrator had to already know and type a raw technical string
   * like "finance.expenses.approve" from memory, and the granted-list
   * showed that same raw string back with no explanation of what it
   * actually does. Grouped by category so the dropdown reads like the
   * spec's own example (FINANCE > Expense Claims > Approve Expense
   * Claims) instead of a flat alphabetical wall of 180 technical keys.
   */
  const catalogue = catalogueQuery.data?.data ?? [];
  const catalogueByCode = new Map(catalogue.map((p) => [p.code, p]));
  const categorized = new Map<string, typeof catalogue>();
  for (const p of catalogue) {
    if (!categorized.has(p.category)) categorized.set(p.category, []);
    categorized.get(p.category)!.push(p);
  }

  const { register, handleSubmit, reset, control, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { scope: 'own' },
  });

  const grantMutation = useMutation({
    mutationFn: (values: FormValues) => rolesApi.grantPermission(id, values),
    onSuccess: () => {
      showSuccess('Permission granted.');
      queryClient.invalidateQueries({ queryKey: ['role-permissions', id] });
      reset();
      setShowForm(false);
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to grant permission.'),
  });

  const revokeMutation = useMutation({
    mutationFn: (permissionKey: string) => rolesApi.revokePermission(id, permissionKey),
    onSuccess: () => {
      showSuccess('Permission revoked.');
      queryClient.invalidateQueries({ queryKey: ['role-permissions', id] });
    },
    // Surfaces the last-admin-capable-role guard's real message if this
    // was the only role still granting admin.users.manage.
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to revoke permission.'),
  });

  return (
    <div className="page">
      <Link to="/admin/roles">&larr; Back to Roles</Link>
      <PageHeader title="Role Permissions" actions={canManage && <button className="btn-primary" onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : 'Grant Permission'}</button>} readOnly={!canManage} />

      {showForm && (
        <form className="inline-form" onSubmit={handleSubmit((values) => grantMutation.mutate(values))}>
          <label>
            Permission
            <Controller
              name="permissionKey"
              control={control}
              render={({ field }) => (
                <select {...field}>
                  <option value="">Select a permission…</option>
                  {Array.from(categorized.entries()).map(([category, perms]) => (
                    <optgroup key={category} label={category}>
                      {perms.map((p) => <option key={p.code} value={p.code}>{p.label}</option>)}
                    </optgroup>
                  ))}
                </select>
              )}
            />
            {errors.permissionKey && <span className="field-error">{errors.permissionKey.message}</span>}
          </label>
          <label>
            Scope
            <select {...register('scope')}>
              {scopes.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <button type="submit" disabled={grantMutation.isPending}>{grantMutation.isPending ? 'Saving…' : 'Grant'}</button>
        </form>
      )}

      {permissionsQuery.isLoading && <div className="loading-state"><span className="loading-spinner" />Loading…</div>}
      {permissionsQuery.isError && <div className="error-state">Could not load permissions.</div>}

      {permissionsQuery.data && (
        <div className="panel">
          <SectionHeader title="Assigned Permissions" />
          <div className="panel-body">
        <DataTable
          rows={permissionsQuery.data.data}
          rowKey={(p) => p.id}
          emptyMessage="This role has no permissions yet."
          columns={[
            {
              header: 'Permission',
              render: (p) => {
                const cataloged = catalogueByCode.get(p.permission_key);
                return cataloged ? (
                  <span title={cataloged.description}>{cataloged.label}</span>
                ) : p.permission_key; // no catalogue entry (shouldn't normally happen) - fall back to the raw key rather than hide it
              },
            },
            { header: 'Scope', render: (p) => p.scope },
            {
              header: '',
              render: (p) =>
                canManage ? (
                  <IconActionButton icon={X} label="Revoke" danger onClick={() => revokeMutation.mutate(p.permission_key)} />
                ) : null,
            },
          ]}
        />
          </div>
        </div>
      )}

      <CommentsThread entityType="role" entityId={id} />

      <DocumentsSection
        documents={docsQuery.data?.data ?? []}
        downloadUrl={(documentId) => genericDocumentApi.downloadUrl('role', documentId)}
        upload={(input) => genericDocumentApi.uploadFor('role', id, input)}
        onDelete={(documentId) => genericDocumentApi.deleteFor('role', documentId)}
        queryKeyToInvalidate={['role-documents', id]}
        hrCategories={false}
      />

      <AuditHistoryTab entityType="role" entityId={id} />
    </div>
  );
}
